// Batch 27 · Add-on 3 · B (M39) — mapping rows kept for accounts not yet in the GL, and accounts still unmapped after an
// import. Own file (FsMapping.jsx is over the 1,500-line rule). EN / AR text here.
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";

export const PM_TEXT = {
  en: { title: "Pending mapping — accounts not yet in the GL", note: "Each row is applied as soon as an import brings the account.",
        code: "Account", name: "Name", fs: "FS mapping", note_m: "Note", file: "From file",
        unmapped: "{n} accounts are still unmapped after this import —", unmappedOne: "1 account is still unmapped after this import —",
        open: "Open mapping", applied: "{n} pending mapping rows were applied by this import." },
  ar: { title: "ربط معلّق — حسابات غير موجودة بعد في دفتر الأستاذ", note: "يُطبَّق كل صف فور أن يُحضر استيراد ما هذا الحساب.",
        code: "الحساب", name: "الاسم", fs: "ربط القوائم المالية", note_m: "الإيضاح", file: "من الملف",
        unmapped: "لا يزال {n} حسابات بلا ربط بعد هذا الاستيراد —", unmappedOne: "لا يزال حساب واحد بلا ربط بعد هذا الاستيراد —",
        open: "افتح الربط", applied: "طُبِّق {n} من صفوف الربط المعلّقة بهذا الاستيراد." },
};
const L_ = (lang) => PM_TEXT[lang === "ar" ? "ar" : "en"];

/** The import summary line: "n rows kept as pending — accounts not yet in the GL" + the list. */
export function PendingImportSummary({ summary }) {
  const { lang } = useI18n();
  if (!summary || !summary.pending_count) return null;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
         data-testid="fs-import-pending">
      <div className="font-medium">{lang === "ar" ? summary.pending_message_ar : summary.pending_message}</div>
      <div className="mt-1 font-mono" dir="ltr">{asArray(summary.pending).join(", ")}</div>
    </div>
  );
}

/** The mapping-status list of pending rows. */
export function PendingMappingPanel({ engId, refreshKey }) {
  const colW = useResizableColumns({ storageKey: "pending-mapping", defaults: [100, 100, 100, 100, 100], mins: [60, 60, 60, 60, 60] });   // Step 11d · add-on
  const { lang } = useI18n();
  const L = L_(lang);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!engId) return;
    api.get(`/fs/${engId}/pending-mapping`, { _silent: true }).then((r) => setRows(asArray(r.data?.pending))).catch(() => setRows([]));
  }, [engId, refreshKey]);
  if (!rows.length) return null;
  return (
    <div className="rounded border border-amber-300 p-3 text-xs" data-testid="fs-pending-panel">
      <div className="font-medium">{L.title} ({rows.length})</div>
      <div className="mb-2 text-[11px] text-muted-foreground">{L.note}</div>
      <ResizableTable cols={colW} wrap className="w-full text-[11px]">
        <thead><tr className="text-start text-muted-foreground">
          <ResizableTh index={0} className="text-start">{L.code}</ResizableTh><ResizableTh index={1} className="text-start">{L.name}</ResizableTh><ResizableTh index={2} className="text-start">{L.fs}</ResizableTh>
          <ResizableTh index={3} className="text-start">{L.note_m}</ResizableTh><ResizableTh index={4} className="text-start">{L.file}</ResizableTh></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.account_code} data-testid={`fs-pending-${r.account_code}`}>
            <td className="font-mono">{r.account_code}</td><td>{r.account_name || "—"}</td><td className="font-mono">{r.fs_mapping || "—"}</td>
            <td className="font-mono">{r.note_mapping || "—"}</td><td>{r.source_file || "—"}</td></tr>))}
        </tbody>
      </ResizableTable>
    </div>
  );
}

/** After a GL / TB import: pending rows applied, and unmapped accounts with the link to mapping. */
export function AfterImportMappingNote({ result, engId }) {
  const { lang } = useI18n();
  const L = L_(lang);
  if (!result) return null;
  const n = Number(result.unmapped_accounts || 0);
  const applied = Number(result.pending_mapping_applied || 0);
  if (!n && !applied) return null;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
         data-testid="gl-after-import-mapping">
      {applied > 0 && <div data-testid="gl-pending-applied">{L.applied.replace("{n}", String(applied))}</div>}
      {n > 0 && (
        <div data-testid="gl-unmapped-left">
          {(n === 1 ? L.unmappedOne : L.unmapped.replace("{n}", String(n)))}{" "}
          <Link to={`/ifrs/${engId}/mapping`} className="font-medium underline" data-testid="gl-unmapped-link">{L.open}</Link>
        </div>
      )}
    </div>
  );
}
